import { groupTreasuryBalances } from '@liquid-spirit/api/treasury-figures';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { credentialScope } from '../utils/credentialScope';
import { useCommunityContext } from './CommunityContext';
import { useAuthContext } from './AuthContext';
import { isBaseChainId } from '../services/WalletService';
import { useInjectedTreasuryWallet } from './treasury/useInjectedTreasuryWallet';
import { useTreasuryFunds } from './treasury/useTreasuryFunds';
import { useTreasuryPaymentIntents } from './treasury/useTreasuryPaymentIntents';
import { useTreasuryWallets } from './treasury/useTreasuryWallets';

const TreasuryContext = createContext(null);

export const TreasuryProvider = ({ children }) => {
  const {token, user} = useAuthContext();
  const {community} = useCommunityContext();
  const communityId = community?._id || community?.id || '';
  const userId = user?._id || user?.id || user?.userId || null;
  return <ScopedTreasuryProvider key={`${credentialScope(token)}:${userId}:${communityId}`} token={token} userId={userId} communityId={communityId}>{children}</ScopedTreasuryProvider>;
};

const ScopedTreasuryProvider = ({children, token, userId, communityId}) => {

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const clearMessages = useCallback(() => {
    setError('');
    setSuccess('');
  }, []);

  const fundState = useTreasuryFunds({
    token,
    userId,
    communityId,
    clearMessages,
    setError,
    setSuccess,
    setSubmitting,
  });

  const walletState = useTreasuryWallets({
    token,
    communityId,
    clearMessages,
    loadFunds: fundState.loadFunds,
    setError,
    setSuccess,
    setSubmitting,
  });

  const injectedWalletState = useInjectedTreasuryWallet({
    clearMessages,
    setError,
    setSuccess,
  });

  const paymentIntentState = useTreasuryPaymentIntents({
    token,
    communityId,
    clearMessages,
    connectedWallet: injectedWalletState.connectedWallet,
    setError,
    setSuccess,
    setSubmitting,
  });

  const value = useMemo(() => ({
    funds: fundState.funds,
    loading: fundState.loading,
    submitting,
    error,
    success,
    isFallbackData: fundState.isFallbackData,
    hasBackendFundsData: fundState.hasBackendFundsData,
    hasLoadedFunds: fundState.hasLoadedFunds,
    walletsByFund: walletState.walletsByFund,
    walletsLoadingFundId: walletState.walletsLoadingFundId,
    walletsErrorByFund: walletState.walletsErrorByFund,
    syncSummaries: walletState.syncSummaries,
    paymentIntents: paymentIntentState.paymentIntents,
    connectedWallet: injectedWalletState.connectedWallet,
    walletAvailable: injectedWalletState.walletAvailable,
    walletEnvironment: injectedWalletState.walletEnvironment,
    walletOnBase: Boolean(injectedWalletState.connectedWallet?.chainId && isBaseChainId(injectedWalletState.connectedWallet.chainId)),
    lastWalletContribution: paymentIntentState.lastWalletContribution,
    fundTotals: groupTreasuryBalances(fundState.funds),
    setFunds: fundState.setFunds,
    loadFunds: fundState.loadFunds,
    loadFundWallets: walletState.loadFundWallets,
    runWalletSync: walletState.runWalletSync,
    setFundReconciliationReview: fundState.setFundReconciliationReview,
    setFundDonationConfig: fundState.setFundDonationConfig,
    updateWalletDonationConfig: walletState.updateWalletDonationConfig,
    loadPaymentIntents: paymentIntentState.loadPaymentIntents,
    createPaymentIntent: paymentIntentState.createPaymentIntent,
    submitPaymentIntent: paymentIntentState.submitPaymentIntent,
    reviewPaymentIntent: paymentIntentState.reviewPaymentIntent,
    connectWallet: injectedWalletState.connectWallet,
    switchWalletToBase: injectedWalletState.switchWalletToBase,
    sendWalletContribution: paymentIntentState.sendWalletContribution,
    addBalance: fundState.addBalance,
    setFundBalance: fundState.setFundBalance,
    createFund: fundState.createFund,
    clearMessages,
  }), [clearMessages, error, fundState, injectedWalletState, paymentIntentState, submitting, success, walletState]);

  return (
    <TreasuryContext.Provider value={value}>
      {children}
    </TreasuryContext.Provider>
  );
};

export const useTreasuryContext = () => {
  const ctx = useContext(TreasuryContext);
  if (!ctx) {
    throw new Error('useTreasuryContext must be used within a TreasuryProvider');
  }
  return ctx;
};
